import {Dispatch, FC, ReactNode, SetStateAction, useEffect, useRef, useState} from 'react'
import axios from 'axios'
import {InvoiceSimpleFormCustomer} from './Customer/InvoiceSimpleFormCustomer'
import {InvoiceSimpleFormSummary} from './InvoiceSimpleFormSummary'
import {
  API_URL,
  isNotEmpty,
  isPOS,
  onTSP,
  numberWithCommas,
} from '../../../../../../_metronic/helpers'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {ListLoading} from '../../../../../../_metronic/helpers/components/ListLoading'
import {SearchSelectOption} from '../../../../../../_metronic/helpers/components/SearchSelect'
import {
  Invoice,
  InvoiceDiscountForm,
  InvoicePaymentForm,
  InvoiceRowForm,
  InvoiceTypeList,
} from '../core/_models'
import {InvoiceItemEditModal} from '../invoice-edit-modal/item/InvoiceItemEditModal'
import {InvoiceNetPrice} from '../invoice-edit-modal/steps/InvoiceNetPrice'
import {UploadedFile} from '_metronic/helpers/components/uploadTypes'
import {Alert} from '../../../../../../_metronic/helpers/components/Alert'
import {useListView} from '../core/ListViewProvider'
import {InvoiceSimpleFormItemTable} from './InvoiceSimpleFormItemTable'
import {useAuth} from '../../../../auth'
import {useUnsavedWork} from '../../../shared/business-switcher/unsavedWork'
import {
  getInvoicePatternValidationError,
  isZeroVatInvoiceTemplate,
} from '../core/invoicePatternValidation'
import {getInvoiceErrorAppLogId, getInvoiceErrorMessage} from '../core/invoiceError'
import {
  getReferencedInvoiceMinimumDate,
  REFERENCED_INVOICE_DATE_HINT,
} from '../core/invoiceReferenceDate'
import {isInvoiceBuyerRequired} from '../core/invoiceBuyerRequirement'
import {
  getWebFormEntryType,
  resolveInvoiceAutoSendControlValue,
} from '../core/invoiceAutoSend'
import {useInvoiceAutoSendDefault} from '../core/useInvoiceAutoSendDefault'
import {InvoiceSubject} from '../core/invoiceSubject'
import {
  getTemplate11RowsError,
  isTemplate11,
  toTemplate11SubmitRow,
} from '../core/invoiceTemplate11'
import {
  hasOrganizationUnitCode,
  resolveInvoiceUnitRule,
  useInvoiceRulesV79,
} from '../core/invoiceRulesV79'
import {WebInvoiceErrorSupportAction} from '../components/WebInvoiceErrorSupportAction'
import {InvoiceIssueDisabledReason} from '../components/InvoiceIssueDisabledReason'
import {
  InvoiceArticle9IssueDateAccessory,
  InvoiceArticle9RegistrationField,
} from '../components/InvoiceArticle9Fields'
import {
  article9UnixMillisecondsToDateTime,
  buildInvoiceArticle9Request,
  getInvoiceArticle9DraftError,
} from '../core/invoiceArticle9'
import {unitLabelOf} from '../core/invoiceUnitCode'

const INVOICE_URL = `${API_URL}/invoices`

type props = {
  invoice: Invoice | null | undefined
  invoiceType: number
  invoiceTemplate: number
  formType: number
  title: {
    title: string
    setTitle: Dispatch<SetStateAction<string>>
  }
}

const InvoiceSimpleFormBody: FC<props> = ({
  invoiceType: _invoiceType,
  invoiceTemplate: _invoiceTemplate,
  invoice,
  formType,
  title,
}) => {
  const {currentUser} = useAuth()
  useUnsavedWork('فرم صورتحساب')
  const {defaultAutoSend} = useInvoiceAutoSendDefault(
    getWebFormEntryType(formType),
    currentUser?.cpi
  )
  const valueAddedNotCalled =
    currentUser?.cpl?.find((company) => company.id == currentUser.cpi)?.vnc == true
  const {itemIdForUpdate, setItemIdForUpdate, variableD, setvariableD} = useListView()
  const minimumReferenceDate = getReferencedInvoiceMinimumDate(invoice, variableD)
  const [first, setFirst] = useState(true)
  const [barcode, setBarcode] = useState('')
  const [serverNo, setServerNo] = useState('')
  const [no, setNo] = useState('')
  const [date, setDate] = useState('')
  const [isArticle9, setIsArticle9] = useState(invoice?.insr === 1)
  const [article9RegistrationDateTime, setArticle9RegistrationDateTime] = useState(() =>
    article9UnixMillisecondsToDateTime(invoice?.indati2m)
  )
  const [currentStep, setCurrentStep] = useState(0)
  const [editInvoiceItem, setEditInvoiceItem] = useState<InvoiceRowForm | null | undefined>(
    undefined
  )
  const [buyer, setBuyer] = useState<string>('')
  const [buyerDescription, setBuyerDescription] = useState<string>('')
  const [buyerSelected, setBuyerSelected] = useState<SearchSelectOption>()
  const [invoiceType, setInvoiceType] = useState<number>(_invoiceType)
  const [invoiceTemplate, setInvoiceTemplate] = useState<number>(_invoiceTemplate)
  const template11 = isTemplate11(invoiceType, invoiceTemplate)
  const effectiveSubject: InvoiceSubject = variableD == 2 ? 2 : variableD == 4 ? 4 : 1
  const isBuyerRequired = isInvoiceBuyerRequired(invoiceType, invoiceTemplate)
  const [contractCode, setContractCode] = useState<string>('')
  const [discounts, setDiscounts] = useState<InvoiceDiscountForm[]>([])
  const [settlement, setSettlement] = useState<string>(invoiceType == 2 ? '1' : '0')
  const {rules: invoiceRules} = useInvoiceRulesV79({
    invoiceType,
    invoicePattern: invoiceTemplate,
    invoiceSubject: effectiveSubject,
    isArticle9,
    settlementMethod: Number(settlement) || undefined,
    buyerType: invoice?.but,
  })
  const unitRule = resolveInvoiceUnitRule(
    invoiceRules,
    invoiceType,
    invoiceTemplate,
    effectiveSubject
  )
  const [payments, setPayments] = useState<InvoicePaymentForm[]>([])
  const [totalVatRate, setTotalVatRate] = useState<number>(0)
  const [description, setDescription] = useState<string>('')
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [items, setItems] = useState<InvoiceRowForm[]>([])
  const [now, setNow] = useState('')
  const [isSend, setIsSend] = useState(false)
  const [autoSendTouched, setAutoSendTouched] = useState(false)
  const article9DraftError = getInvoiceArticle9DraftError(
    isArticle9,
    invoiceType,
    article9RegistrationDateTime,
    date,
    now
  )
  const article9Request = buildInvoiceArticle9Request(
    isArticle9,
    invoiceType,
    article9RegistrationDateTime
  )

  const displayedIsSend = resolveInvoiceAutoSendControlValue(
    invoice?.id ? undefined : defaultAutoSend,
    isSend,
    autoSendTouched
  )

  const setIsSendFromControl: Dispatch<SetStateAction<boolean>> = (value) => {
    setAutoSendTouched(true)
    setIsSend(value)
  }

  useEffect(() => {
    if (buyerSelected) {
      setBuyer(buyerSelected.value)
    } else {
      setBuyer('')
    }
  }, [buyerSelected])
  const S = (v: unknown) => (v == null ? '' : String(v))
  useEffect(() => {
    if (first) {
      if (invoice && invoice.id) {
        setNo(invoice.no || '')
        setDate(invoice.idt || '')
        setIsArticle9(invoice.insr === 1)
        setArticle9RegistrationDateTime(article9UnixMillisecondsToDateTime(invoice.indati2m))
        setInvoiceType(invoice.typi || 1)
        setInvoiceTemplate(invoice.tmpi || 1)
        setBuyer(invoice.buci?.toString() || '')
        setBuyerDescription(invoice.bud || '')
        setContractCode(invoice.cnc || '')
        setSettlement(invoice.stli?.toString() || '')
        setDescription(invoice.dsc || '')
        setFiles(invoice.fls || [])
        setTotalVatRate(invoice.tvr || 0)
        if (invoice.stli) {
          setSettlement(invoice.stli.toString() || (invoiceType == 2 ? '1' : '0'))
        }
        if (invoice.pym) {
          let _payments: InvoicePaymentForm[] = []
          invoice.pym.forEach((rw) => {
            _payments.push({
              id: rw.id || 0,
              description: rw.dsc || '',
              price: rw.prc || 0,
              cash: rw.csh || 0,
              vat: rw.vt || 0,
              date: rw.dt || '',
              type: (rw.typ || 0).toString(),
              refereceNo: rw.trc || '',
              traceNo: rw.trc || '',
              cardNo: rw.cno || '',
              payerId: rw.nid || '',
              payerNationalCode: rw.nid || '',
              switchNumber: rw.swno || rw.switchNumber || '',
              acquirerNumber: rw.aqno || rw.acquirerNumber || '',
              terminalNumber: rw.terno || rw.terminalNumber || '',
              files: rw.fl || [],
              taxValidationError: rw.txerr || rw.taxValidationError || '',
            })
          })
          setPayments([...[], ..._payments])
        }
        if (invoice.tdis) {
          let _discounts: InvoiceDiscountForm[] = []
          invoice.tdis.forEach((rw) => {
            _discounts.push({
              id: rw.id || 0,
              description: rw.dsc || '',
              price: rw.prc || 0,
              rate: (rw.rt || 0).toString(),
              type: (rw.rt || 0) > 0 ? 'rate' : 'price',
            })
          })
          setDiscounts([...[], ..._discounts])
        }
        if (invoice.itm) {
          let _items: InvoiceRowForm[] = []
          invoice.itm.forEach((rw) => {
            const forceZeroVat =
              valueAddedNotCalled || isZeroVatInvoiceTemplate(invoice.tmpi || invoiceTemplate)
            const rowVatRate = forceZeroVat ? 0 : rw.vtr
            const rowVatPrice = forceZeroVat ? 0 : parseFloat(S(rw.vtp).replace(/,/g, ''))
            _items.push({
              product: {
                id: rw.sop.id,
                cd: rw.sop.cd,
                nm: rw.sop.nm,
                vtr: rowVatRate,
              },
              description: rw.dsc,
              amount: parseFloat(S(rw.amt).replace(/,/g, '')),
              qty: parseFloat(S(rw.qty).replace(/,/g, '')),
              price: parseFloat(S(rw.prc).replace(/,/g, '')),
              adPrice: parseFloat(S(rw.adp).replace(/,/g, '')),
              ...(isTemplate11(invoice.typi || 1, invoice.tmpi || 1)
                ? {adis: parseFloat(S(rw.adp).replace(/,/g, ''))}
                : {}),
              netPrice: parseFloat(S(rw.ntp).replace(/,/g, '')),
              vatPrice: rowVatPrice,
              discount: parseFloat(S(rw.dis).replace(/,/g, '')),
              wage: rw.wge && rw.wge != null ? parseFloat(S(rw.wge).replace(/,/g, '')) : 0,
              commission: rw.comm && rw.comm != null ? parseFloat(S(rw.comm).replace(/,/g, '')) : 0,
              profit: rw.prft && rw.prft != null ? parseFloat(S(rw.prft).replace(/,/g, '')) : 0,
              carat: rw.cara && rw.cara != null ? parseFloat(S(rw.cara).replace(/,/g, '')) : 0,
              govTaxName: rw.gtn,
              govTaxPrice: parseFloat(S(rw.gtp).replace(/,/g, '')),
              govTaxRate: rw.gtr,
              taxName: rw.txn,
              taxPrice: parseFloat(S(rw.txp).replace(/,/g, '')),
              taxRate: rw.txr,
              productId: rw.sop.id,
              vatRate: rowVatRate,
              id: rw.id,
              unit: {
                label: unitLabelOf(rw.unt, rw.uni, rw),
                value: rw.uni,
              },
            })
          })
          setItems([...[], ..._items])
        }
        setTitle()
      } else {
        axios.get(`${API_URL}/invoices/checkNo/`).then((response) => {
          setServerNo(response.data)
          setNo(response.data)
        })
      }
      axios.get(`${API_URL}/taxpayer/getdatetime/`).then((response) => {
        setNow(response.data)
      })
      setFirst(false)
    }
  }, [])

  useEffect(() => {
    if (!(invoice && invoice.id)) {
      setDate(now)
    }
  }, [now])

  useEffect(() => {
    if (invoiceType === 3 && isArticle9) {
      setIsArticle9(false)
    }
  }, [invoiceType, isArticle9])

  const setTitle = () => {
    let _title = ''
    const selectedType = InvoiceTypeList.find((x) => x.id == invoiceType)
    if (selectedType) {
      let name = selectedType.name
      if (selectedType.template) {
        name += ' - ' + selectedType.template.find((x) => x.id == invoiceTemplate)?.name
      }
      _title = name
    }
    if (variableD == 4) {
      _title = `برگشت از فروش صورتحساب ${no} ${date}`
    } else if (variableD == 2) {
      _title = `اصلاح ${_title} ${no} ${date}`
    } else if (variableD == 5) {
      _title = `عملیات پرداخت ${_title} ${no} ${date}`
    } else {
      if (invoice && invoice.id) {
        _title = `ویرایش ${_title} ${no} ${date}`
      } else {
        _title = `صدور ${_title}`
      }
    }
    title.setTitle(_title)
  }

  useEffect(() => {
    if (no != serverNo) {
      setServerNo('')
    }
  }, [no])

  const getItem = (rw: InvoiceRowForm) => {
    // if (!first && returnQty) {
    //   const finded = returnQty.find((x) => x.id == rw.id)
    //   if (finded) {
    //     return finded
    //   }
    // }
    return rw
  }
  const getSumDiscount: () => number = () => {
    let sum = 0

    return sum
  }
  const getSumPrice: () => number = () => {
    let sum = 0
    items.map((rw) => {
      sum += getItem(rw).price || 0
      sum += (getItem(rw).wage || 0) + (getItem(rw).profit || 0) + (getItem(rw).commission || 0)
    })
    return sum
  }
  const getSumItemDiscount = () => {
    let sum = 0
    items.map((rw) => {
      sum += getItem(rw).discount || 0
    })
    return sum
  }
  const getSumAdPrice: () => number = () => {
    return getSumPrice() - getSumItemDiscount() - getSumDiscount()
  }
  const getSumAdPriceEXDiscount: () => number = () => {
    return getSumPrice() - getSumItemDiscount()
  }
  const getSumVatPrice: () => number = () => {
    let sum = 0
    items.map((rw) => {
      sum += getItem(rw).vatPrice || 0
    })
    return sum
  }
  const getSumTaxPrice: () => number = () => {
    let sum = 0
    return sum
  }
  const getSumNetPrice: () => number = () => {
    let sum = 0
    sum = getSumAdPrice() + getSumVatPrice() + getSumTaxPrice()
    return sum
  }

  const getPatternValidationError = () => getInvoicePatternValidationError(invoiceTemplate, items)

  const patternValidationError = getPatternValidationError()
  const issueDisabledReasons = [
    ...(isBuyerRequired && !isNotEmpty(buyerSelected) ? ['خریدار صورتحساب را انتخاب کنید.'] : []),
    ...(patternValidationError ? [patternValidationError] : []),
    ...(article9DraftError ? [article9DraftError] : []),
  ]
  const isIssueDisabled = issueDisabledReasons.length > 0

  const [loading, setLoading] = useState<boolean>(false)
  const submittingRef = useRef(false)
  const [errorModal, setErrorModal] = useState('')
  const [errorAppLogId, setErrorAppLogId] = useState<number>()
  const [ebtalNewModal, setEbtalNewModal] = useState(false)
  const [modifyModal, setModifyModal] = useState(false)

  const save = (execute: boolean, confirm: boolean, save: boolean) => {
    if (loading || submittingRef.current) return

    if (article9DraftError) {
      setErrorModal(article9DraftError)
      return
    }
    const patternError = getPatternValidationError()
    if (patternError != '') {
      setErrorModal(patternError)
      return
    }
    if (unitRule.required) {
      const invalidUnitIndex = items.findIndex((item) => !hasOrganizationUnitCode(item.unit))
      if (invalidUnitIndex >= 0) {
        setErrorModal(
          `واحد اندازه‌گیری قلم شماره ${invalidUnitIndex + 1} الزامی است؛ واحدی را انتخاب کنید که کد معتبر سامانه مؤدیان داشته باشد.`
        )
        return
      }
    }

    if (template11) {
      const template11Error =
        getTemplate11RowsError(items) ||
        (settlement == '3'
          ? 'در الگوی بورس کالا، روش تسویه نقد و نسیه (با سهم نقدی) فقط در فرم کامل صورتحساب قابل ثبت است.'
          : '')
      if (template11Error) {
        setErrorModal(template11Error)
        return
      }
    }

    submittingRef.current = true
    setLoading(true)
    axios
      .post(`${INVOICE_URL}/update/${itemIdForUpdate || ''}`, {
        no: no,
        //serverNo: serverNo,
        date: date,
        type: invoiceType,
        template: invoiceTemplate,
        webFormType: formType,
        items: template11 ? items.map(toTemplate11SubmitRow) : items,
        buyerDescription: buyerDescription,
        description: description,
        buyer: buyer,
        settlement: settlement,
        action: variableD,
        execute,
        confirm,
        save,
        payments: template11 ? [] : payments,
        isArticle9: article9Request.isArticle9,
        indati2m: article9Request.indati2m,
        isSend:
          !invoice?.id && !autoSendTouched
            ? defaultAutoSend
            : isSend,
      })
      .then((response) => {
        if (isPOS) {
          window.location.href = '/invoicePrint/' + response.data
        } else {
          window.location.href = '/apps/invoice-management'
        }
      })
      .catch((error) => {
        submittingRef.current = false
        setLoading(false)
        const status = error.response?.status
        if (status == 426) {
          setEbtalNewModal(true)
        } else if (status == 415) {
          setModifyModal(true)
        } else if (status == 409) {
          //setPaymentModal(error.response.data)
        } else {
          //console.log(error.response)
          setErrorAppLogId(getInvoiceErrorAppLogId(error))
          setErrorModal(
            getInvoiceErrorMessage(error, 'ذخیره صورتحساب ناموفق بود. لطفاً دوباره تلاش کنید.')
          )
        }
      })
  }

  useEffect(() => {
    setTitle()
  }, [invoiceType, invoiceTemplate])

  return (
    <>
      <div className='row'>
        {currentStep == 0 && (
          <>
            <div className='col-5'>
              <CustomInputs
                type='text'
                label={
                  <>
                    شماره مسلسل {variableD == 2 ? 'اصلاحی' : variableD == 4 ? 'برگشت از فروش' : ''}
                  </>
                }
                stateValue={{value: no, setValue: setNo}}
              />
            </div>
            <div className='col-7'>
              {isPOS ? (
                <CustomInputs
                  type='text'
                  max={16}
                  LTR
                  inputClass='text-left ltr'
                  label={
                    <>
                      تاریخ و زمان صدور صورتحساب{' '}
                      {variableD == 2 ? 'اصلاحی' : variableD == 4 ? 'برگشت از فروش' : ''}
                    </>
                  }
                  labelAccessory={
                    <InvoiceArticle9IssueDateAccessory
                      invoiceType={invoiceType}
                      issueDateTime={date}
                      isArticle9={isArticle9}
                      setIsArticle9={setIsArticle9}
                    />
                  }
                  stateValue={{value: date, setValue: setDate}}
                />
              ) : (
                <CustomInputs
                  type='datepicker'
                  label={
                    <>
                      تاریخ و زمان صدور صورتحساب{' '}
                      {variableD == 2 ? 'اصلاحی' : variableD == 4 ? 'برگشت از فروش' : ''}
                    </>
                  }
                  labelAccessory={
                    <InvoiceArticle9IssueDateAccessory
                      invoiceType={invoiceType}
                      issueDateTime={date}
                      isArticle9={isArticle9}
                      setIsArticle9={setIsArticle9}
                    />
                  }
                  timePicker
                  isRequired
                  limitFromDate={minimumReferenceDate}
                  limitToDate={now}
                  stateValue={{value: date, setValue: setDate}}
                  hint={minimumReferenceDate ? REFERENCED_INVOICE_DATE_HINT : undefined}
                />
              )}
            </div>
            {(isArticle9 || invoiceType === 3) && (
              <div className='col-7 offset-5 mt-5'>
                <InvoiceArticle9RegistrationField
                  invoiceType={invoiceType}
                  issueDateTime={date}
                  now={now}
                  isArticle9={isArticle9}
                  setIsArticle9={setIsArticle9}
                  registrationDateTime={article9RegistrationDateTime}
                  setRegistrationDateTime={setArticle9RegistrationDateTime}
                />
              </div>
            )}
            <div className='col-12 mt-10'>
              <InvoiceSimpleFormItemTable
                items={items}
                setEditInvoiceItem={setEditInvoiceItem}
                invoiceTemplate={invoiceTemplate}
              />
              <div>
                <InvoiceNetPrice getSumNetPrice={getSumNetPrice} />
              </div>
            </div>
          </>
        )}
        {currentStep == 1 && !(variableD > 1) && (
          <InvoiceSimpleFormCustomer
            buyerSelected={{buyerSelected: buyerSelected, setBuyerSelected: setBuyerSelected}}
            step={{step: currentStep, setStep: setCurrentStep}}
            isBuyerRequired={isBuyerRequired}
            invoiceTemplate={invoiceTemplate}
          />
        )}
        {currentStep == 2 && (
          <>
            <InvoiceSimpleFormSummary
              getSumNetPrice={getSumNetPrice}
              no={no}
              date={date}
              buyerSelected={buyerSelected}
              items={items}
              description={{
                description: description,
                setDescription: setDescription,
              }}
              invoiceType={invoiceType}
              settlement={{
                settlement: settlement,
                setSettlement: setSettlement,
              }}
              invoiceTemplate={invoiceTemplate}
            />
            <div className='mt-5 rounded border bg-light p-4'>
              <CustomInputs
                type='toggle'
                isVertical
                label='ارسال مستقیم صورتحساب به سامانه مؤدیان پس از ثبت'
                stateValue={{value: displayedIsSend, setValue: setIsSendFromControl}}
              />
              <div className='text-gray-600 fs-7 mt-2'>
                مقدار اولیه از تنظیم «
                {formType == 2 ? 'فرم وب قیمت تمام‌شده' : 'فرم وب ساده'}» کسب‌وکار گرفته
                شده است و تغییر این گزینه فقط روی همین صورتحساب اثر دارد.
              </div>
            </div>
          </>
        )}
      </div>
      <div className='mp-invoice-form-actions-spacer'></div>
      {getSumNetPrice() > 0 && (
        <nav className='navbar navbar-light bg-light text-center shadow mp-invoice-form-actions'>
          <div className='container-fluid'>
            <div className='btn-toolbar w-100'>
              <div className='btn-group d-flex justify-content-between w-100' role='group'>
                {formType == 2 ? (
                  <>
                    <button
                      type='button'
                      className='btn flex-grow-1 p-0'
                      disabled={loading || isIssueDisabled}
                      onClick={() => {
                        save(false, false, false)
                      }}
                    >
                      <div className='d-flex flex-column'>
                        <i className='las la-save fs-2x'></i>
                        {onTSP ? <>صدور و ارسال صورتحساب</> : <>صدور صورتحساب</>}
                      </div>
                    </button>
                    <InvoiceIssueDisabledReason reasons={issueDisabledReasons} />
                  </>
                ) : (
                  <>
                    {currentStep > 0 && (
                      <button
                        type='button'
                        className='btn btn-outline-secondary w-10px'
                        onClick={() => {
                          let stp = currentStep
                          if (variableD > 1) {
                            if (stp == 2) {
                              stp = 0
                            }
                          } else {
                            stp = currentStep - 1
                          }
                          setCurrentStep(stp)
                        }}
                      >
                        <div className='d-flex flex-column'>
                          <i className='la la-arrow-right fs-3x'></i>
                          مرحله قبل
                        </div>
                      </button>
                    )}
                    {currentStep < 2 && (
                      <button
                        type='button'
                        className='btn btn-outline-secondary flex-grow-1'
                        onClick={() => {
                          let stp = currentStep
                          if (variableD > 1) {
                            if (stp == 0) {
                              stp = 2
                            }
                          } else {
                            stp = currentStep + 1
                          }
                          setCurrentStep(stp)
                        }}
                      >
                        <div className='d-flex flex-column'>
                          <i className='la la-arrow-left fs-3x'></i>
                          مرحله بعد
                        </div>
                      </button>
                    )}
                    {currentStep == 2 && (
                      <>
                        <button
                          type='button'
                          className='btn btn-primary flex-grow-1 p-0'
                          disabled={loading || isIssueDisabled}
                          onClick={() => {
                            save(false, false, false)
                          }}
                        >
                          <div className='d-flex flex-column'>
                            <i className='las la-save fs-2x'></i>
                            {onTSP ? <>صدور و ارسال صورتحساب</> : <>صدور صورتحساب</>}
                          </div>
                        </button>
                        <InvoiceIssueDisabledReason reasons={issueDisabledReasons} />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </nav>
      )}
      {editInvoiceItem !== undefined && (
        <InvoiceItemEditModal
          items={{
            items: items,
            setItems: setItems,
          }}
          invoiceItem={{invoiceItem: editInvoiceItem, setInvoiceItem: setEditInvoiceItem}}
          invoiceTemplate={invoiceTemplate}
          invoiceType={invoiceType}
          invoiceSubject={effectiveSubject}
          invoiceDate={date}
          formType={formType}
          rules={invoiceRules}
        />
      )}
      {loading && <ListLoading text='در حال صدور صورتحساب...' />}
      {errorModal != '' && (
        <Alert
          icon='la-exclamation-triangle'
          title='خطا'
          body={
            <WebInvoiceErrorSupportAction
              errorMessage={errorModal}
              formType={formType}
              invoiceType={invoiceType}
              invoiceTemplate={invoiceTemplate}
              invoiceNumber={no}
              invoice={invoice}
              appLogId={errorAppLogId}
              onNavigate={() => {
                setErrorModal('')
                setErrorAppLogId(undefined)
              }}
            />
          }
          cancel={{
            text: 'بستن',
            fn: () => {
              setErrorModal('')
              setErrorAppLogId(undefined)
            },
          }}
        />
      )}
      {ebtalNewModal && (
        <Alert
          icon='la-exclamation-triangle'
          type='danger'
          title='توجه'
          body={
            <p className='text-gray-600 fs-4'>
              برای تغییر این اطلاعات، باید صورتحساب را ابطال کنید. برای این کار، از ستون عملیات در
              جدول، گزینه «ابطال» را انتخاب کنید.
            </p>
          }
          cancel={{
            text: 'انصراف',
            fn: () => {
              setEbtalNewModal(false)
            },
          }}
          // ok={{
          //   text: 'تایید',
          //   fn: () => {
          //     setEbtalNewModal(false)
          //     save(false, true)
          //   },
          // }}
        />
      )}
      {modifyModal && (
        <Alert
          icon='la-info'
          title=''
          body={
            <p className='text-gray-600 fs-4'>
              مطابق دستورالعمل سامانه مؤدیان، تغییراتی که در صورتحساب خود اعمال کرده‌اید منجر به
              صدور <span className='text-gray-800 fw-bolder mx-2'>صورتحساب اصلاحی</span> خواهد شد.
            </p>
          }
          type='warning'
          cancel={{
            text: 'انصراف',
            fn: () => {
              setModifyModal(false)
            },
          }}
          ok={{
            text: 'تایید',
            fn: () => {
              setModifyModal(false)
              save(false, true, false)
            },
          }}
        />
      )}
    </>
  )
}

export {InvoiceSimpleFormBody}
